import { GhettoStream, MuLawDecoder, PcmAudioPlayer } from 'ui3';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import H264Controls from './H264Controls';
import JMuxer from 'jmuxer';
import { processStream } from './processStream';
/* eslint-disable jsx-a11y/media-has-caption */
import { useHistoryVideo } from '@/app/main/subadmin/livestreaming/history/HistoryProvider';

const H264VideoPlayer = ({ url, roomData, setIsLoading }) => {
	const playerRef = useRef(null);
	const boxRef = useRef(null);
	const { volume, setVolume } = useHistoryVideo();
	const [error, setError] = useState('');
	const [isSeeking, setIsSeeking] = useState(false);
	const [pendingSeekTime, setPendingSeekTime] = useState(0);
	const [isBuffering, setIsBuffering] = useState(false);
	const pcmPlayerRef = useRef(null);
	const volumeRef = useRef(volume);
	const jmuxerRef = useRef(null);
	const lastFrameUtcRef = useRef(null);
	const isSpeedChange = useRef(false);
	const isPausedRef = useRef(false);
	const abortControllerRef = useRef(null);
	const [seekOffset, setSeekOffset] = useState(0);
	const [currentSliderValue, setCurrentSliderValue] = useState(0);
	const [speed, setSpeed] = useState(100);

	const maxDuration = 1200;

	// Extract initialPosition from the url's 'pos' parameter
	const initialPosition = useMemo(() => {
		const urlObj = new URL(url);
		const posParam = urlObj.searchParams.get('pos');
		return posParam ? parseInt(posParam, 10) : null;
	}, [url]);

	const [position, setPosition] = useState(initialPosition);

	// Update position when initialPosition changes
	useEffect(() => {
		setPosition(initialPosition);
		setPendingSeekTime(0); // Reset pending seek time
		setIsSeeking(false); // Reset seeking status
	}, [initialPosition]);

	useEffect(() => {
		if (position !== null && initialPosition !== null) {
			const newSeekOffset = (position - initialPosition) / 1000; // Convert to seconds
			setSeekOffset(newSeekOffset);
		}
	}, [position, initialPosition]);

	useEffect(() => {
		const videoElement = playerRef.current;
		if (!videoElement) return;

		const updateSliderValue = () => {
			const videoCurrentTime = videoElement.currentTime;
			const sliderValue = videoCurrentTime + seekOffset;
			setCurrentSliderValue(sliderValue);
		};

		videoElement.addEventListener('timeupdate', updateSliderValue);

		return () => videoElement.removeEventListener('timeupdate', updateSliderValue);
	}, [playerRef, seekOffset]);

	const convertSliderValueToTimestamp = useCallback(
		(sliderValue) => {
			if (initialPosition === null) {
				console.error('Initial position is not set.');
				return null;
			}
			// Convert slider value (seconds) to timestamp (milliseconds)
			// accounting for playback speed as well since we are simulating a 20 minute segment
			const newPosition = initialPosition + (sliderValue / (speed / 100)) * 1000;
			return newPosition;
		},
		[initialPosition, speed]
	);

	const getStreamUrl = useCallback(() => {
		const urlObj = new URL(url);

		if (isSpeedChange.current) {
			urlObj.searchParams.set('pos', lastFrameUtcRef.current);
			isSpeedChange.current = false;
		} else if (position !== null) {
			urlObj.searchParams.set('pos', position);
		} else {
			urlObj.searchParams.set('pos', initialPosition);
		}

		if (speed) {
			urlObj.searchParams.set('speed', speed);
		}

		return urlObj.toString();
	}, [url, position, speed, initialPosition]);

	const isVideoPlaying = (video) => {
		if (video) {
			return !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > video.HAVE_FUTURE_DATA);
		}
		return false;
	};

	const callStreamEnded = useCallback((abortController) => {
		return (reader) => {
			if (abortController) {
				abortController.abort();
			}

			if (reader) {
				const cancelPromise = reader.cancel('Streaming canceled');
				if (cancelPromise && cancelPromise.catch) {
					cancelPromise.catch((e) => {
						if (e && (e.code === DOMException.ABORT_ERR || e.code === DOMException.INVALID_STATE_ERR)) {
							// Expected result. Suppress error.
						} else {
							console.error(e);
						}
					});
				}
				reader = null;
			}
		};
	}, []);

	const callFrameCallback = useCallback((jmuxer, pcmPlayer, muLawDecoder, player) => {
		let init = false;

		return (frame) => {
			if (frame.isVideo) {
				lastFrameUtcRef.current = frame.meta.utc;
				jmuxer.feed({
					video: frame.frameData,
				});

				// Start playing once we have some data streaming in.
				if (!init && !isVideoPlaying(player)) {
					const playPromise = player.play();

					if (playPromise !== undefined) {
						playPromise.then(() => {}).catch(() => {});
					}

					init = true;
				}
			} else if (frame.isAudio) {
				// The only supported format is mu-law encoding with one audio channel.
				if (frame.format.wFormatTag === 7 && frame.format.nChannels === 1) {
					// 7 is mu-law, mu-law is 8 bits per sample but decodes to 16 bits per sample.
					const pcm16Bit = muLawDecoder.DecodeUint8ArrayToFloat32Array(frame.frameData);
					pcmPlayer.AcceptBuffer([pcm16Bit], frame.format.nSamplesPerSec);
				} else if (frame.format.wFormatTag === 61868) {
					pcmPlayer.DecodeAndPlayAudioData(frame.frameData, frame.format.nSamplesPerSec);
				} else {
					console.log('Unsupported audio frame format', frame.format, frame);
				}
			}
		};
	}, []);

	// Update volumeRef when volume changes
	useEffect(() => {
		volumeRef.current = volume;
		if (pcmPlayerRef.current) {
			pcmPlayerRef.current.SetAudioVolumeFromSettings(volume);
		}
		if (playerRef.current) {
			playerRef.current.volume = volume;
		}
	}, [volume]);

	const fetchStream = useCallback(
		(streamUrl, fetchArgs, player, abortController) => {
			if (jmuxerRef.current) {
				jmuxerRef.current.destroy();
				jmuxerRef.current = null;
			}

			if (pcmPlayerRef.current) {
				pcmPlayerRef.current.Reset();
			}

			const stream = new GhettoStream();
			const jmuxer = new JMuxer({
				node: player,
				mode: 'video',
				maxDelay: 1000,
				flushingTime: 1000,
				clearBuffer: true,
				debug: false,
				fps: 30,
			});

			jmuxer.cancelDelay = function () {
				if (
					this.node.buffered &&
					this.node.buffered.length > 0 &&
					!this.node.seeking &&
					!this.node.paused // This condition needs to be added to the jmuxer.cancelDelay function in order for the video to pause/unpause correctly in our implementation
				) {
					const end = this.node.buffered.end(0);
					if (end - this.node.currentTime > this.options.maxDelay / 1000) {
						this.node.currentTime = end - 0.001;
					}
				}
			};

			jmuxerRef.current = jmuxer;
			const pcmPlayer = new PcmAudioPlayer();
			// Save the pcmPlayer to the ref so volume/play/pause can be toggled from the controls
			pcmPlayerRef.current = pcmPlayer;
			const muLawDecoder = new MuLawDecoder();
			pcmPlayer.SetAudioVolumeFromSettings(volumeRef.current);

			const onEndStream = callStreamEnded(abortController);
			const frameCallback = callFrameCallback(jmuxerRef.current, pcmPlayer, muLawDecoder, player);

			fetch(streamUrl, fetchArgs)
				.then((res) => {
					const contentType = res.headers.get('Content-Type');
					if (res.status === 200 && contentType === 'video/mpeg') {
						setError('');
						const reader = res.body.getReader();
						return processStream(stream, reader, frameCallback, onEndStream, isPausedRef);
					} else {
						setError('Failed to load stream');
						onEndStream();
					}
				})
				.catch((e) => {
					if (e === 'New time selected') {
						return;
					}
					setError('Server is unreachable');
					console.error('Fetch error:', e);
					onEndStream();
				})
				.finally(() => setIsLoading(false));
		},
		[callStreamEnded, callFrameCallback, setIsLoading]
	);

	useEffect(() => {
		const abortController = new AbortController();
		abortControllerRef.current = abortController;
		const videoElement = playerRef.current;

		if (videoElement) {
			const fetchArgs = { credentials: 'same-origin', signal: abortController.signal };
			const streamUrl = getStreamUrl();

			if (!streamUrl) {
				console.error('Stream URL is invalid. Aborting stream fetch.');
				return;
			}

			fetchStream(streamUrl, fetchArgs, videoElement, abortController);

			videoElement.onloadeddata = () => {
				videoElement
					.play()
					.then(() => setIsBuffering(false))
					.catch((e) => console.error('Error attempting to play video:', e));
			};

			videoElement.onwaiting = () => {
				setIsBuffering(true);
			};

			videoElement.oncanplay = () => {
				if (isBuffering) {
					setIsBuffering(false);
					videoElement
						.play()
						.then(() => setIsBuffering(false))
						.catch((e) => console.error('Error attempting to resume video:', e));
				}
			};

			return () => {
				if (pcmPlayerRef.current) {
					pcmPlayerRef.current.Reset();
				}
				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
				}
				videoElement.onloadeddata = null;
				videoElement.onwaiting = null;
				videoElement.oncanplay = null;
			};
		}
	}, [position, speed, url, getStreamUrl]);

	const handleSliderChangeCommitted = useCallback(
		(event, newValue) => {
			const videoElement = playerRef.current;
			if (videoElement) {
				videoElement.pause();
			}
			setIsSeeking(false);
			setPendingSeekTime(0);

			const newPosition = convertSliderValueToTimestamp(newValue);
			setSeekOffset((newPosition - initialPosition) / 1000);
			setPosition(newPosition); // Triggers stream reload
		},
		[convertSliderValueToTimestamp, playerRef, initialPosition, speed]
	);

	const handleSliderChange = useCallback((event, newValue) => {
		setPendingSeekTime(newValue / (speed / 100));
		setIsSeeking(true);
	}, []);

	const handleSliderChangeStart = () => setIsSeeking(true);

	const handleSkip = useCallback(
		(seconds) => {
			const videoElement = playerRef.current;
			if (videoElement) {
				videoElement.pause();

				const sliderValue = currentSliderValue * (speed / 100) + seconds;
				const clampedSliderValue = Math.max(0, Math.min(sliderValue, maxDuration * (speed / 100)));

				setPendingSeekTime(clampedSliderValue);
				setIsSeeking(false);

				const newPosition = convertSliderValueToTimestamp(clampedSliderValue);
				setSeekOffset((newPosition - initialPosition) / 1000);
				setPosition(newPosition); // Triggers stream reload
			}
		},
		[playerRef, currentSliderValue, convertSliderValueToTimestamp, maxDuration, initialPosition, speed]
	);

	const handleVolumeChange = (newVolume) => {
		setVolume(newVolume);

		if (pcmPlayerRef.current) {
			pcmPlayerRef.current.SetAudioVolumeFromSettings(newVolume);
		}
		if (playerRef.current) {
			playerRef.current.volume = newVolume;
		}
	};

	const handlePlaybackSpeedChange = useCallback(
		(newSpeed) => {
			const videoElement = playerRef.current;
			if (videoElement) {
				// Get the current playback position in seconds
				const currentVideoTime = videoElement.currentTime;
				videoElement.pause();

				// Calculate the new position in milliseconds
				const newPosition = initialPosition + (currentVideoTime + seekOffset) * 1000;

				setSpeed(newSpeed);
				setPosition(newPosition);
				setSeekOffset((newPosition - initialPosition) / 1000);
			}
		},
		[initialPosition, playerRef, seekOffset]
	);

	useEffect(() => {
		const videoElement = playerRef.current;

		const handlePlay = () => {
			isPausedRef.current = false;
			pcmPlayerRef.current?.Resume();
		};

		const handlePause = () => {
			pcmPlayerRef.current?.Pause();
			isPausedRef.current = true;
		};

		if (videoElement) {
			videoElement.addEventListener('play', handlePlay);
			videoElement.addEventListener('pause', handlePause);
		}

		return () => {
			if (videoElement) {
				videoElement.removeEventListener('play', handlePlay);
				videoElement.removeEventListener('pause', handlePause);
			}
		};
	}, []);

	return (
		<>
			{error ? <h3>{error}</h3> : null}

			<div className="relative overflow-hidden" ref={boxRef}>
				<video className="rounded-lg history-video-player" id="history-video" ref={playerRef} />

				<div className="mx-auto relative">
					<H264Controls
						boxRef={boxRef}
						currentTime={isSeeking ? pendingSeekTime / (speed / 100) : currentSliderValue}
						handleSkip={handleSkip}
						handleSliderChange={handleSliderChange}
						handleSliderChangeCommitted={handleSliderChangeCommitted}
						handleSliderChangeStart={handleSliderChangeStart}
						initialPosition={initialPosition}
						maxDuration={maxDuration}
						onPlaybackSpeedChange={handlePlaybackSpeedChange}
						onVolumeChange={handleVolumeChange}
						playbackSpeed={speed}
						playerRef={playerRef}
						room={roomData}
					/>
				</div>
			</div>
		</>
	);
};

export default H264VideoPlayer;
